import React, { useState, useRef, useEffect } from 'react';
import { Submission } from '../../../types';
import { SignificationMediaDisplay } from './SignificationMediaDisplay';
import './MediaDisplay.css';

/* ── Badge colours per media type ── */
const TYPE_META: Record<string, { color: string; label: string }> = {
  photo:         { color: '#7A93B5', label: 'Photo' },
  video:         { color: '#6E7A5A', label: 'Video' },
  audio:         { color: '#C2AC8E', label: 'Audio' },
  text:          { color: '#7BBFAF', label: 'Text' },
  screen:        { color: '#7A93B5', label: 'Screen' },
  signification: { color: '#6E7A5A', label: 'Signal' },
};

/* ── Duration formatting ── */
function fmtDuration(secs: number): string {
  if (!secs || isNaN(secs)) return '0:00';
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

const WAVE_BARS = 32;

/* Safari (all of iPhone) cannot decode WebM/Opus — the format every
   Android/Chrome participant records. canPlayType is NOT trustworthy here:
   Safari answers "maybe" and then stalls loading forever without firing an
   error (observed live 2026-08-04 — endless spinner, no event). So Safari is
   excluded by identification, not capability. */
const IS_SAFARI = typeof navigator !== 'undefined'
  && /safari/i.test(navigator.userAgent)
  && !/chrome|crios|fxios|edg|android/i.test(navigator.userAgent);
export const WEBM_OPUS_PLAYABLE = typeof document === 'undefined'
  ? true
  : !IS_SAFARI && !!document.createElement('audio').canPlayType('audio/webm; codecs="opus"');
export const isWebmUrl = (url: string): boolean => /\.webm(\?|$)/.test(url);
export const WEBM_AUDIO_FALLBACK_TEXT =
  "This recording can't play in Safari — open in Chrome, or read the transcript.";

export interface MediaDisplayProps {
  submission: Submission;
  className?: string;
}

export const MediaDisplay: React.FC<MediaDisplayProps> = ({ submission, className }) => {
  const meta = TYPE_META[submission.mediaType] || TYPE_META.audio;
  const mediaUrls = submission.mediaUrls || [];
  const hasMultiple = mediaUrls.length > 1;

  /* Expand/collapse tall media (portrait > 4:5) */
  const [mediaExpanded, setMediaExpanded] = useState(false);
  const [isTallMedia, setIsTallMedia] = useState(false);

  /* Carousel state */
  const [activeIndex, setActiveIndex] = useState(0);
  const [touchStartX, setTouchStartX] = useState<number | null>(null);

  /* Audio that errored at decode/load time (Safari lies via canPlayType) */
  const [audioBroken, setAudioBroken] = useState(false);

  /* Per-image load tracking */
  const [loadedUrls, setLoadedUrls] = useState<Set<string>>(new Set());
  const handleImgLoad = (url: string, naturalW?: number, naturalH?: number) => {
    setLoadedUrls(prev => { const n = new Set(prev); n.add(url); return n; });
    if (naturalW && naturalH && naturalH / naturalW > 5 / 4) {
      setIsTallMedia(true);
    }
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStartX(e.touches[0].clientX);
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX === null) return;
    const diff = touchStartX - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) {
      if (diff > 0 && activeIndex < mediaUrls.length - 1) setActiveIndex(prev => prev + 1);
      else if (diff < 0 && activeIndex > 0) setActiveIndex(prev => prev - 1);
    }
    setTouchStartX(null);
  };

  /* Playback state */
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioProgress, setAudioProgress] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const [videoProgress, setVideoProgress] = useState(0);

  useEffect(() => {
    return () => {
      if (audioRef.current) { audioRef.current.pause(); audioRef.current.src = ''; }
      if (videoRef.current) { videoRef.current.pause(); videoRef.current.src = ''; }
    };
  }, []);

  const toggleVideo = () => {
    if (!videoRef.current) return;
    if (isPlaying) videoRef.current.pause();
    else videoRef.current.play().catch(() => {});
    setIsPlaying(!isPlaying);
  };

  const toggleAudio = () => {
    if (!audioRef.current) return;
    if (isPlaying) audioRef.current.pause();
    else audioRef.current.play().catch(() => {});
    setIsPlaying(!isPlaying);
  };

  /** Switch clips: stop the current one and clear its playback state, so the
      new clip starts from zero rather than inheriting the old progress. */
  const selectClip = (next: number) => {
    audioRef.current?.pause();
    setIsPlaying(false);
    setAudioProgress(0);
    setAudioDuration(0);
    setAudioBroken(false);
    setActiveIndex(next);
  };

  const currentUrl = mediaUrls[activeIndex];
  const isAudio = submission.mediaType === 'audio';
  const isText = submission.mediaType === 'text';
  const isSignification = submission.mediaType === 'signification';

  if (isSignification) {
    return (
      <div className={`md-media ${className || ''}`}>
        <SignificationMediaDisplay submission={submission} />
      </div>
    );
  }

  if (isAudio) {
    // audioBroken: canPlayType can answer "maybe" and still fail to decode
    // (Safari), so a real playback error also flips to the fallback.
    if (currentUrl && isWebmUrl(currentUrl) && (!WEBM_OPUS_PLAYABLE || audioBroken)) {
      return (
        <div className={`md-audio-row ${className || ''}`} style={{ cursor: 'default' }}>
          <span style={{ fontSize: '12px', opacity: 0.75 }}>{WEBM_AUDIO_FALLBACK_TEXT}</span>
        </div>
      );
    }
    return (
      <div className={`md-audio-row ${className || ''}`} onClick={toggleAudio}>
        <button className="md-audio-play" onClick={(e) => { e.stopPropagation(); toggleAudio(); }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="white">
            {isPlaying ? (
              <>
                <rect x="6" y="4" width="4" height="16" rx="1" />
                <rect x="14" y="4" width="4" height="16" rx="1" />
              </>
            ) : (
              <polygon points="6,3 20,12 6,21" />
            )}
          </svg>
        </button>

        <div className="md-waveform">
          {Array.from({ length: WAVE_BARS }).map((_, i) => (
            <div
              key={i}
              className={`md-wbar ${(i / WAVE_BARS) <= audioProgress ? 'md-wbar-played' : ''}`}
              style={{ height: `${14 + Math.sin(i * 0.5) * 10 + (i % 3) * 4}px` }}
            />
          ))}
        </div>

        <span className="md-audio-time">{fmtDuration(audioDuration)}</span>

        {/* Clip stepper. Audio returns before the frame carousel below, so
            without this every clip after the first is unreachable. */}
        {hasMultiple && (
          <div className="md-audio-clips" onClick={(e) => e.stopPropagation()}>
            <button
              className="md-audio-clip-nav"
              aria-label="Previous clip"
              disabled={activeIndex === 0}
              onClick={() => selectClip(activeIndex - 1)}
            >‹</button>
            <span className="md-audio-clip-count">{activeIndex + 1}/{mediaUrls.length}</span>
            <button
              className="md-audio-clip-nav"
              aria-label="Next clip"
              disabled={activeIndex === mediaUrls.length - 1}
              onClick={() => selectClip(activeIndex + 1)}
            >›</button>
          </div>
        )}

        {currentUrl && (
          <audio
            ref={audioRef}
            src={currentUrl}
            onLoadedMetadata={(e) => setAudioDuration(e.currentTarget.duration)}
            onTimeUpdate={(e) => {
              const ct = e.currentTarget;
              if (ct.duration) setAudioProgress(ct.currentTime / ct.duration);
            }}
            onEnded={() => { setIsPlaying(false); setAudioProgress(0); }}
            onError={() => setAudioBroken(true)}
          />
        )}
      </div>
    );
  }

  return (
    <div
      className={`md-media ${isTallMedia && !mediaExpanded ? 'md-media--capped' : ''} ${isTallMedia && mediaExpanded ? 'md-media--expanded' : ''} ${className || ''}`}
      onTouchStart={hasMultiple ? handleTouchStart : undefined}
      onTouchEnd={hasMultiple ? handleTouchEnd : undefined}
    >
      {/* Photo */}
      {submission.mediaType === 'photo' && (
        currentUrl ? (
          <div className="md-img-wrap">
            {!loadedUrls.has(currentUrl) && <div className="md-img-shimmer md-shimmer" />}
            <img
              src={currentUrl}
              alt="Submission"
              className={`md-media-img ${loadedUrls.has(currentUrl) ? 'md-img-visible' : 'md-img-loading'}`}
              onLoad={(e) => {
                const img = e.currentTarget;
                handleImgLoad(currentUrl, img.naturalWidth, img.naturalHeight);
              }}
            />
          </div>
        ) : (
          <div className="md-media-placeholder md-media-steel"><PhotoIcon /></div>
        )
      )}

      {/* Video / Screen */}
      {(submission.mediaType === 'video' || submission.mediaType === 'screen') && (
        <div className="md-media-video-wrap" onClick={toggleVideo}>
          {currentUrl ? (
            <>
              {!loadedUrls.has(currentUrl) && <div className="md-img-shimmer md-shimmer" />}
              <video
                ref={videoRef}
                src={currentUrl}
                className={`md-media-video ${loadedUrls.has(currentUrl) ? 'md-img-visible' : 'md-img-loading'}`}
                playsInline
                onLoadedData={() => {
                  // No dimensions on purpose: videos never enter the 4:5
                  // crop/expand mechanism (cover would crop content, and the
                  // expanded state distorts against max-height). They always
                  // letterbox via the base rule (contain, ≤70vh).
                  handleImgLoad(currentUrl);
                }}
                onTimeUpdate={(e) => {
                  const v = e.currentTarget;
                  if (v.duration) setVideoProgress(v.currentTime / v.duration);
                }}
                onEnded={() => { setIsPlaying(false); setVideoProgress(0); }}
              />
            </>
          ) : (
            <div className="md-media-placeholder md-media-dark" />
          )}
          {!isPlaying && (
            <div className="md-play-btn">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                <polygon points="6,3 20,12 6,21" />
              </svg>
            </div>
          )}
          {submission.mediaType === 'screen' && (
            <span className="md-screen-badge">SCREEN</span>
          )}
          {/* Thin progress bar */}
          {isPlaying && videoProgress > 0 && (
            <div className="md-video-progress">
              <div className="md-video-progress-fill" style={{ width: `${videoProgress * 100}%` }} />
            </div>
          )}
        </div>
      )}

      {/* Text */}
      {isText && (
        <div className="md-media-placeholder md-media-text md-media-seafoam">
          <div className="md-text-preview">
            {submission.notes
              ? `\u201C${submission.notes}\u201D`
              : '\u201CResponse submitted\u201D'}
          </div>
        </div>
      )}

      {/* Type badge */}
      <span className="md-type-badge" style={{ background: meta.color }}>
        {meta.label}
      </span>

      {/* Counter badge (top right) */}
      {hasMultiple && (
        <div className="md-counter">
          {activeIndex + 1}/{mediaUrls.length}
        </div>
      )}

      {/* Prev/next arrows — the only navigation reachable with a mouse; the
          swipe handlers above are touch-only. Hidden at the ends. */}
      {hasMultiple && activeIndex > 0 && (
        <button
          className="md-nav md-nav-prev"
          aria-label="Previous media"
          onClick={(e) => { e.stopPropagation(); setActiveIndex(i => i - 1); }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
      )}
      {hasMultiple && activeIndex < mediaUrls.length - 1 && (
        <button
          className="md-nav md-nav-next"
          aria-label="Next media"
          onClick={(e) => { e.stopPropagation(); setActiveIndex(i => i + 1); }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M9 18l6-6-6-6" />
          </svg>
        </button>
      )}

      {/* Dots indicator */}
      {hasMultiple && (
        <div className="md-dots">
          {mediaUrls.map((_, i) => (
            <button
              key={i}
              className={`md-dot ${i === activeIndex ? 'md-dot-active' : ''}`}
              onClick={(e) => { e.stopPropagation(); setActiveIndex(i); }}
            />
          ))}
        </div>
      )}

      {/* Expand/collapse hint for tall media */}
      {isTallMedia && (
        <>
          {!mediaExpanded && <div className="md-expand-fade" />}
          <button
            className="md-expand-btn"
            onClick={(e) => { e.stopPropagation(); setMediaExpanded(prev => !prev); }}
            aria-label={mediaExpanded ? 'Collapse' : 'Expand'}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round">
              {mediaExpanded
                ? <path d="M18 15l-6-6-6 6" />
                : <path d="M6 9l6 6 6-6" />
              }
            </svg>
          </button>
        </>
      )}
    </div>
  );
};

const PhotoIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="1.5" strokeLinecap="round">
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <circle cx="8.5" cy="8.5" r="1.5" />
    <polyline points="21,15 16,10 5,21" />
  </svg>
);

/** Timestamp formatting — shared utility */
export function formatTimestamp(raw: any): { relative: string; date: string; receipt: string } {
  if (!raw) return { relative: '', date: '', receipt: '' };
  const date = raw.toDate ? raw.toDate() : new Date(raw);
  if (isNaN(date.getTime())) return { relative: '', date: '', receipt: '' };

  const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const timeStr = date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

  const ms = Date.now() - date.getTime();
  if (ms < 0) return { relative: 'Just now', date: dateStr, receipt: `Submitted at ${timeStr}` };
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return { relative: 'Just now', date: dateStr, receipt: `Submitted at ${timeStr}` };
  if (mins < 60) return { relative: `${mins}m ago`, date: dateStr, receipt: `Submitted at ${timeStr}` };
  const hours = Math.floor(mins / 60);
  if (hours < 24) return { relative: `${hours}h ago`, date: dateStr, receipt: `Submitted at ${timeStr}` };
  const days = Math.floor(hours / 24);
  if (days < 7) return { relative: `${days}d ago`, date: dateStr, receipt: '' };
  return { relative: dateStr, date: '', receipt: '' };
}
